import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  CalendarDays,
  CarFront,
  CheckCircle2,
  ClipboardList,
  Clock,
  MapPin,
  Receipt,
  Shield,
  User,
  Users,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import bookingService from "../../../api/services/bookingService";
import { useToast } from "../../../hooks/useToast";
import {
  formatBookingDateTime,
  formatBookingDuration,
} from "../../../utils/bookingWindow";

const toNumber = (value) => {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).replace(/[^0-9.-]/g, "");
  if (!cleaned) return 0;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateValue = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB");
};

const formatCurrency = (value, currency = "₦") =>
  `${currency}${toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const mapBookingStatus = (status) => {
  const normalized = String(status || "").trim().toLowerCase();

  if (["pending_assistant_action", "pending"].includes(normalized)) {
    return "Pending";
  }

  if (
    ["accepted", "assistant_arrived", "confirmed", "completed"].includes(
      normalized
    )
  ) {
    return "Confirmed";
  }

  if (
    ["rejected", "cancelled", "canceled", "failed_payment"].includes(normalized)
  ) {
    return "Canceled";
  }

  return status || "Pending";
};

const resolveCustomerName = (item = {}) => {
  const customer =
    item?.user || item?.customer || item?.client || item?.guest || item?.traveler || {};

  return (
    customer?.name ||
    customer?.fullName ||
    [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
    item?.customerName ||
    item?.guestName ||
    "—"
  );
};

const resolveLocation = (item = {}) =>
  item?.tour?.title ||
  item?.tour?.name ||
  item?.service?.name ||
  item?.service?.title ||
  item?.experience ||
  item?.title ||
  item?.location ||
  item?.city ||
  item?.state ||
  "—";

const resolveParticipants = (item = {}) =>
  toNumber(
    item?.touristsCount ||
      item?.participants ||
      item?.participantsCount ||
      item?.participants_count ||
      item?.guests ||
      item?.guestCount ||
      item?.guest_count
  );

const resolveCheckIn = (item = {}) =>
  item?.arrivalDate ||
  item?.startDate ||
  item?.start_date ||
  item?.checkInDate ||
  item?.check_in ||
  item?.fromDate ||
  item?.from_date ||
  item?.date ||
  item?.scheduledAt ||
  item?.scheduled_at ||
  item?.createdAt ||
  item?.created_at ||
  "";

const resolveCheckOut = (item = {}, fallbackValue = "") =>
  item?.departureDate ||
  item?.endDate ||
  item?.end_date ||
  item?.checkOutDate ||
  item?.check_out ||
  item?.toDate ||
  item?.to_date ||
  fallbackValue;

const resolveAmount = (item = {}) =>
  item?.amountExpected ||
  item?.amountPaid ||
  item?.amount ||
  item?.totalAmount ||
  item?.total_amount ||
  item?.price ||
  item?.paidAmount ||
  item?.paid_amount ||
  0;

const sumAddOnSubtotals = (items = []) =>
  Array.isArray(items)
    ? items.reduce((sum, item) => sum + toNumber(item?.subtotal), 0)
    : 0;

const formatLogisticsValue = (value) => {
  if (!value) return "—";

  if (typeof value === "string" || typeof value === "number") {
    const normalized = String(value).trim();
    return normalized || "—";
  }

  if (Array.isArray(value)) {
    const normalized = value
      .map((item) => {
        if (!item) return "";
        if (typeof item === "string" || typeof item === "number") {
          return String(item).trim();
        }
        const label =
          item?.label ||
          item?.name ||
          item?.type ||
          item?.vehicle ||
          item?.title ||
          "";
        const quantity = item?.quantity ?? item?.count ?? item?.total ?? "";
        if (label && quantity) return `${quantity} ${label}`;
        return label || String(quantity || "").trim();
      })
      .filter(Boolean);

    return normalized.length ? normalized.join(", ") : "—";
  }

  if (typeof value === "object") {
    const normalized = Object.entries(value)
      .map(([key, raw]) => {
        if (raw === null || raw === undefined || raw === "") return "";
        if (typeof raw === "number" || /^[0-9]+$/.test(String(raw).trim())) {
          return `${raw} ${key.replace(/[_-]+/g, " ")}`;
        }
        return `${key.replace(/[_-]+/g, " ")}: ${raw}`;
      })
      .filter(Boolean);

    return normalized.length ? normalized.join(", ") : "—";
  }

  return "—";
};

const normalizeBooking = (item = {}, index = 0) => {
  const checkIn = resolveCheckIn(item);
  const checkOut = resolveCheckOut(item, checkIn);

  return {
    id:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      item?.paymentReference ||
      `booking-${index}`,
    bookingId:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      item?.paymentReference ||
      null,
    customerName: resolveCustomerName(item),
    location: resolveLocation(item),
    participants: resolveParticipants(item),
    checkIn,
    checkOut,
    checkInLabel: formatDate(checkIn),
    checkOutLabel: formatDate(checkOut),
    amountLabel: formatCurrency(resolveAmount(item)),
    status: mapBookingStatus(item?.status),
    rawItem: item,
  };
};

const normalizeBookingsPayload = (payload) => {
  const source = payload?.data ?? payload ?? {};
  const items =
    source?.items ||
    source?.results ||
    source?.bookings ||
    source?.data ||
    source?.rows ||
    [];

  if (!Array.isArray(items)) return [];

  return items.map((item, index) => normalizeBooking(item, index));
};

const normalizeBookingDetail = (payload, fallbackBooking) => {
  const source = payload?.data ?? payload ?? {};
  const booking = source?.booking || source?.data || source || {};
  const item =
    booking && typeof booking === "object" && Object.keys(booking).length
      ? booking
      : fallbackBooking?.rawItem || {};

  const customer =
    item?.user || item?.customer || item?.client || item?.guest || item?.traveler || {};

  const checkIn = resolveCheckIn(item);
  const checkOut = resolveCheckOut(item, checkIn);
  const securityOfficers =
    item?.securityOfficers ||
    item?.security_officers ||
    item?.securityPersonnel ||
    item?.security_personnel ||
    item?.securityService ||
    item?.security_service ||
    item?.logistics?.securityOfficers ||
    item?.logistics?.securityPersonnel ||
    item?.logistics?.security ||
    item?.addons?.security ||
    null;

  const convoyVehicles =
    item?.escortVehicles ||
    item?.convoyVehicles ||
    item?.convoy_vehicles ||
    item?.vehicles ||
    item?.vehicleTypes ||
    item?.logistics?.convoyVehicles ||
    item?.logistics?.vehicles ||
    item?.addons?.vehicles ||
    null;

  const vehicleCost = sumAddOnSubtotals(item?.escortVehicles);
  const securityCost = sumAddOnSubtotals(item?.securityPersonnel);
  const total = resolveAmount(item) || resolveAmount(fallbackBooking?.rawItem);
  const baseTripCost = Math.max(0, total - vehicleCost - securityCost);

  return {
    bookingId:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      item?.paymentReference ||
      fallbackBooking?.bookingId ||
      "—",
    customerName:
      customer?.name ||
      customer?.fullName ||
      [customer?.firstName, customer?.lastName].filter(Boolean).join(" ").trim() ||
      fallbackBooking?.customerName ||
      "—",
    customerEmail: customer?.email || customer?.contactEmail || "—",
    customerPhone: customer?.phone || customer?.mobile || customer?.contactPhone || "—",
    location: resolveLocation(item) || fallbackBooking?.location || "—",
    participants: resolveParticipants(item) || fallbackBooking?.participants || 0,
    // A request is accepted or turned down on these two lines, so they carry
    // the hour as well as the day.
    checkInLabel: formatBookingDateTime(checkIn || fallbackBooking?.checkIn),
    checkOutLabel: formatBookingDateTime(checkOut || fallbackBooking?.checkOut),
    durationLabel: formatBookingDuration(item),
    bookedOnLabel: formatBookingDateTime(item?.createdAt),
    reference:
      item?.paymentReference || item?._id || fallbackBooking?.bookingId || "—",
    amountLabel: formatCurrency(resolveAmount(item)),
    status: mapBookingStatus(item?.status || fallbackBooking?.status),
    itinerary: item?.itinerary || fallbackBooking?.rawItem?.itinerary || "",
    securityOfficersLabel: formatLogisticsValue(securityOfficers),
    convoyVehiclesLabel: formatLogisticsValue(convoyVehicles),
    baseTripCostLabel: formatCurrency(baseTripCost),
    vehicleCostLabel: formatCurrency(vehicleCost),
    securityCostLabel: formatCurrency(securityCost),
    totalLabel: formatCurrency(total),
  };
};

const DetailRow = ({ icon: Icon, label, value }) => (
    <div className="flex items-start justify-between gap-4 text-xs">
      <div className="flex items-center gap-2 text-[#667085]">
      {Icon ? <Icon className="h-3.5 w-3.5 shrink-0" /> : null}
      <span>{label}</span>
    </div>
    <span className="max-w-[50%] text-right font-medium text-[#7A054A]">
      {value || "—"}
    </span>
  </div>
);

const DetailTextBlock = ({ icon: Icon, label, value }) => {
  if (!value) return null;

  return (
    <div className="space-y-2 rounded-xl border border-[#EEF0F3] bg-[#F9FAFB] px-3 py-3">
      <div className="flex items-center gap-2 text-xs font-semibold text-[#667085]">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span>{label}</span>
      </div>
      <p className="whitespace-pre-wrap text-xs font-medium leading-5 text-[#7A054A]">
        {value}
      </p>
    </div>
  );
};

export default function Bookings() {
  const toast = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  // Tracks *that* the load failed so the table can say so; the reason itself is
  // toasted, not printed into the row.
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [bookingDetail, setBookingDetail] = useState(null);
  const [actionLoading, setActionLoading] = useState("");
  const [showAcceptSuccess, setShowAcceptSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  // The "new booking request" email links straight to one booking; open its
  // drawer once the list has loaded so the assistant can act without hunting
  // for the row.
  const deepLinkBookingId = searchParams.get("booking");
  const deepLinkOpened = useRef(false);

  useEffect(() => {
    let active = true;

    const fetchBookings = async () => {
      setLoading(true);
      setLoadFailed(false);

      try {
        const response = await bookingService.getBookings({ page: 1, limit: 100 });
        if (!active) return;
        setBookings(normalizeBookingsPayload(response));
      } catch (err) {
        if (!active) return;
        setBookings([]);
        setLoadFailed(true);
        toast.apiError(err, "Unable to load bookings right now.");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchBookings();

    return () => {
      active = false;
    };
  }, [toast]);

  const sortedBookings = useMemo(
    () =>
      [...bookings].sort((a, b) => {
        const checkInA = parseDateValue(a.checkIn)?.getTime() || 0;
        const checkInB = parseDateValue(b.checkIn)?.getTime() || 0;
        return checkInB - checkInA;
      }),
    [bookings]
  );

  const handleViewDetails = async (booking) => {
    setSelectedBooking(booking);
    setBookingDetail(normalizeBookingDetail({}, booking));
    setDetailLoading(Boolean(booking?.bookingId));
    setShowAcceptSuccess(false);

    if (!booking?.bookingId) {
      setDetailLoading(false);
      return;
    }

    try {
      const response = await bookingService.getBookingById(booking.bookingId);
      setBookingDetail(normalizeBookingDetail(response, booking));
    } catch (err) {
      toast.apiError(err, "Unable to load booking details right now.");
    } finally {
      setDetailLoading(false);
    }
  };

  useEffect(() => {
    if (deepLinkOpened.current || !deepLinkBookingId || loading) return;

    const match = bookings.find(
      (booking) => String(booking.bookingId) === String(deepLinkBookingId)
    );
    if (!match) return;

    deepLinkOpened.current = true;
    handleViewDetails(match);
    // handleViewDetails is re-created each render; the ref guard keeps this to
    // a single open, so it is intentionally left out of the dependency list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings, deepLinkBookingId, loading]);

  const closeDetails = () => {
    setSelectedBooking(null);
    setBookingDetail(null);
    setDetailLoading(false);
    setActionLoading("");
    setShowAcceptSuccess(false);
  };

  const updateBookingStatus = (bookingId, nextStatus) => {
    setBookings((prev) =>
      prev.map((booking) =>
        booking.id === bookingId ? { ...booking, status: nextStatus } : booking
      )
    );
    setSelectedBooking((prev) => (prev ? { ...prev, status: nextStatus } : prev));
    setBookingDetail((prev) => (prev ? { ...prev, status: nextStatus } : prev));
  };

  const handlePendingAction = async (action) => {
    if (!selectedBooking?.bookingId) return;

    setActionLoading(action);

    try {
      if (action === "accept") {
        await bookingService.acceptBooking(selectedBooking.bookingId);
        updateBookingStatus(selectedBooking.id, "Confirmed");
        setShowAcceptSuccess(true);
        return;
      }

      await bookingService.rejectBooking(selectedBooking.bookingId);
      // Mark the row before closing — otherwise the list keeps showing the
      // booking as Pending until the page is reloaded.
      updateBookingStatus(selectedBooking.id, "Canceled");
      closeDetails();
    } catch (err) {
      toast.apiError(
        err,
        action === "accept"
          ? "Unable to accept this booking."
          : "Unable to decline this booking."
      );
    } finally {
      setActionLoading("");
    }
  };

  return (
    <>
      <div className="space-y-4">
        <div className="rounded-xl border border-[#EEF0F3] bg-white">
          <div className="border-b border-[#EEF0F3] px-4 py-4">
            <h1 className="text-2xl font-semibold text-[#1D2939]">Bookings</h1>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-[900px] w-full text-base">
              <thead className="border-b border-[#EEF0F3] text-left">
                <tr>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Location
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Participants
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Check-in
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Check-out
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-[#667085]">
                    Status
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#EEF0F3]">
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-base font-medium text-[#667085]"
                    >
                      Loading bookings...
                    </td>
                  </tr>
                ) : loadFailed ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-base font-medium text-[#667085]"
                    >
                      Couldn't load bookings.
                    </td>
                  </tr>
                ) : sortedBookings.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-base font-medium text-[#667085]"
                    >
                      No bookings found.
                    </td>
                  </tr>
                ) : (
                  sortedBookings.map((booking) => (
                    <tr key={booking.id}>
                      <td className="px-4 py-3 text-base font-medium text-[#344054]">
                        {booking.customerName}
                      </td>
                      <td className="px-4 py-3 text-base font-medium text-[#475467]">
                        {booking.location}
                      </td>
                      <td className="px-4 py-3 text-base font-medium text-[#475467]">
                        {booking.participants || "—"}
                      </td>
                      <td className="px-4 py-3 text-base font-medium text-[#475467]">
                        {booking.checkInLabel}
                      </td>
                      <td className="px-4 py-3 text-base font-medium text-[#475467]">
                        {booking.checkOutLabel}
                      </td>
                      <td className="px-4 py-3 text-base font-medium text-[#475467]">
                        {booking.amountLabel}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => handleViewDetails(booking)}
                          className="text-sm font-semibold text-[#7A054A] transition hover:underline"
                        >
                          View details
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selectedBooking ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          {showAcceptSuccess ? (
            <div className="w-full max-w-md rounded-[24px] bg-white px-6 py-8 text-center shadow-2xl">
              <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-[#E7F6EA]">
                <CheckCircle2 className="h-10 w-10 text-[#19913A]" />
              </div>

              <h2 className="mt-6 text-[30px] font-semibold leading-tight text-[#7A054A]">
                Thanks for Accepting the Request
              </h2>
              <p className="mx-auto mt-4 max-w-sm text-base leading-7 text-[#667085]">
                50% of the payment will be disbursed into your account when the
                trip starts.
              </p>

              <button
                type="button"
                onClick={closeDetails}
                className="mt-8 w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029]"
              >
                Done
              </button>
            </div>
          ) : (
            <div className="w-full max-w-md rounded-[20px] bg-white shadow-2xl">
              <div className="px-5 pb-5 pt-4">
                <button
                  type="button"
                  onClick={closeDetails}
                  className="mb-3 inline-flex rounded-full p-1.5 text-[#101828] transition hover:bg-[#F2F4F7]"
                  aria-label="Back"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>

                <div className="space-y-5">
                  <h2 className="text-base font-semibold text-[#101828]">
                    Booking Details
                  </h2>

                  {detailLoading ? (
                    <p className="text-sm font-medium text-[#667085]">
                      Loading booking details...
                    </p>
                  ) : (
                    <>
                      <div className="space-y-3">
                        <DetailRow
                          icon={User}
                          label="Name"
                          value={bookingDetail?.customerName || "—"}
                        />
                        <DetailRow
                          icon={Users}
                          label="Participants"
                          value={bookingDetail?.participants || "—"}
                        />
                        <DetailRow
                          icon={MapPin}
                          label="Location"
                          value={bookingDetail?.location || "—"}
                        />
                        <DetailRow
                          icon={CalendarDays}
                          label="Check-in"
                          value={bookingDetail?.checkInLabel || "—"}
                        />
                        <DetailRow
                          icon={CalendarDays}
                          label="Check-out"
                          value={bookingDetail?.checkOutLabel || "—"}
                        />
                        <DetailRow
                          icon={Clock}
                          label="Duration"
                          value={bookingDetail?.durationLabel || "—"}
                        />
                        <DetailRow
                          icon={Receipt}
                          label="Reference"
                          value={bookingDetail?.reference || "—"}
                        />
                        <DetailRow
                          icon={CalendarDays}
                          label="Booked on"
                          value={bookingDetail?.bookedOnLabel || "—"}
                        />
                      </div>

                      <DetailTextBlock
                        icon={ClipboardList}
                        label="Itinerary"
                        value={bookingDetail?.itinerary || "—"}
                      />

                      <div className="space-y-3">
                        <h3 className="text-base font-semibold text-[#101828]">
                          Logistics
                        </h3>
                        <DetailRow
                          icon={Shield}
                          label="Security Officers"
                          value={bookingDetail?.securityOfficersLabel || "—"}
                        />
                        <DetailRow
                          icon={CarFront}
                          label="Convoy Vehicles"
                          value={bookingDetail?.convoyVehiclesLabel || "—"}
                        />
                      </div>

                      <div className="space-y-3">
                        <h3 className="text-base font-semibold text-[#101828]">
                          Payment Summary
                        </h3>
                        <DetailRow
                          label="Base Trip Cost"
                          value={bookingDetail?.baseTripCostLabel || "₦0.00"}
                        />
                        <DetailRow
                          label="Vehicle Cost"
                          value={bookingDetail?.vehicleCostLabel || "₦0.00"}
                        />
                        <DetailRow
                          label="Security Cost"
                          value={bookingDetail?.securityCostLabel || "₦0.00"}
                        />
                        <DetailRow
                          label="Total"
                          value={bookingDetail?.totalLabel || "₦0.00"}
                        />
                      </div>
                    </>
                  )}

                  {!detailLoading ? (
                    selectedBooking.status === "Pending" ? (
                      <div className="space-y-2">
                        <button
                          type="button"
                          onClick={() => handlePendingAction("accept")}
                          disabled={Boolean(actionLoading)}
                          className="w-full rounded-md bg-[#61013B] px-4 py-3 text-sm font-semibold text-white transition hover:bg-[#450029] disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {actionLoading === "accept" ? "Accepting..." : "Accept"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePendingAction("decline")}
                          disabled={Boolean(actionLoading)}
                          className="w-full rounded-md border border-[#C97AAA] px-4 py-3 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F8EEF4] disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {actionLoading === "decline" ? "Declining..." : "Decline"}
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={closeDetails}
                        className="w-full rounded-md border border-[#C97AAA] px-4 py-3 text-sm font-semibold text-[#7A054A] transition hover:bg-[#F8EEF4]"
                      >
                        Close
                      </button>
                    )
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </>
  );
}
